import { useState } from 'react';

import type { Property, PropertyType } from '@noti/shared';

import { t } from '../i18n';
import { ROW_BACKGROUNDS, TYPE_LABEL } from './database-rows';
import {
  ActionMenu,
  ActionMenuInput,
  ActionMenuItem,
  ActionMenuRadioGroup,
  ActionMenuRadioItem,
} from './ActionMenu';
import type { DatabaseTableProps } from './DatabaseTable';
import { SWATCH_LINE } from './DatabaseRowMenu';
import { blockBackgroundVar } from './extensions';
import { Glyph } from './icons';

export const ADDABLE: PropertyType[] = [
  'text',
  'number',
  'select',
  'status',
  'multi',
  'person',
  'date',
  'checkbox',
  'url',
  'email',
  'phone',
  'created',
  'edited',
];

const OPTION_TYPES: PropertyType[] = ['select', 'status', 'multi'];
const GROUPABLE_TYPES: PropertyType[] = ['select', 'status', 'person', 'checkbox'];

export type ColumnMenuActions = Pick<
  DatabaseTableProps,
  | 'onSort'
  | 'onFilter'
  | 'onGroupBy'
  | 'onHide'
  | 'onColumnColor'
  | 'onColumnType'
  | 'onRenameColumn'
  | 'onOptionsText'
  | 'onClearColumn'
  | 'onDuplicateColumn'
  | 'onInsertColumn'
  | 'onDeleteColumn'
  | 'onAddProperty'
>;

interface DatabaseColumnMenuProps {
  property: Property;
  properties: Property[];
  /** Sort direction mark shown next to the column name. */
  sortMark: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: ColumnMenuActions;
}

function optionsTextOf(property: Property): string {
  return property.options.map((option) => option.n).join(', ');
}

export function DatabaseColumnMenu({
  property,
  properties,
  sortMark,
  open,
  onOpenChange,
  table,
}: DatabaseColumnMenuProps): React.ReactElement {
  const [name, setName] = useState(property.name);
  const [optionsText, setOptionsText] = useState(optionsTextOf(property));
  const [type, setType] = useState<PropertyType>(property.type);
  const [colorOpen, setColorOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const isTitle = property.type === 'title';
  const hasOptions = OPTION_TYPES.includes(property.type);
  const groupable = GROUPABLE_TYPES.includes(property.type);

  const changeOpen = (next: boolean): void => {
    if (next) {
      setName(property.name);
      setOptionsText(optionsTextOf(property));
      setType(property.type);
      setColorOpen(false);
      setTypeOpen(false);
      setNewName('');
    }
    onOpenChange(next);
  };

  const pickType = (value: string): void => {
    const picked = ADDABLE.find((option) => option === value);
    if (!picked) return;
    table.onColumnType(property.id, picked);
    setType(picked);
    setTypeOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className="eg eg--h"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('database.columnOptions')}
        title={t('database.columnOptionsTitle')}
        onClick={() => changeOpen(!open)}
      >
        <span className="eg__gb" />
      </button>
      <ActionMenu
        open={open}
        onOpenChange={changeOpen}
        label={t('database.propertyMenu')}
        className="menu--column"
        align="start"
        toggleSelector=".eg--h"
        trigger={
          <button type="button" className="row dbh__btn" title={property.name}>
            <span className="dbh__icon">{property.icon ?? ''}</span>
            <span className="dbh__name">{property.name}</span>
            <span className="dbh__arrow">{sortMark}</span>
          </button>
        }
      >
        <ActionMenuInput
          className="cin dbmenu__name"
          value={name}
          aria-label={t('database.propertyName')}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            const trimmed = name.trim();
            if (trimmed && trimmed !== property.name) table.onRenameColumn(property.id, trimmed);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />

        {isTitle ? null : (
          <>
            <ActionMenuItem
              keepOpen
              className="menu__item nb dbmenu__typebtn"
              aria-expanded={typeOpen}
              onSelect={() => setTypeOpen((value) => !value)}
            >
              <Glyph d="M5 6V4h14v2 M12 4v16 M9 20h6" size={16} />
              <span className="dbmenu__typelabel">{TYPE_LABEL[type]}</span>
              <Glyph d="M6 9l6 6 6-6" size={12} strokeWidth={2} className="dd__chev" />
            </ActionMenuItem>
            {typeOpen ? (
              <div className="dbmenu__typelist">
                <ActionMenuRadioGroup value={type} onValueChange={pickType}>
                  {ADDABLE.map((option) => (
                    <ActionMenuRadioItem key={option} value={option}>
                      <span className="menu__mark" aria-hidden="true">
                        {option === type ? '✓' : ''}
                      </span>
                      {TYPE_LABEL[option]}
                    </ActionMenuRadioItem>
                  ))}
                </ActionMenuRadioGroup>
              </div>
            ) : null}
          </>
        )}

        {hasOptions ? (
          <ActionMenuInput
            className="cin dbmenu__name"
            value={optionsText}
            aria-label={t('database.optionsComma')}
            placeholder={t('database.optionsComma')}
            onChange={(event) => setOptionsText(event.target.value)}
            onBlur={() => table.onOptionsText(property.id, optionsText)}
          />
        ) : null}

        <ActionMenuItem onSelect={() => table.onSort({ propId: property.id, dir: 'asc' })}>
          {t('database.sortAscending')}
        </ActionMenuItem>
        <ActionMenuItem onSelect={() => table.onSort({ propId: property.id, dir: 'desc' })}>
          {t('database.sortDescending')}
        </ActionMenuItem>
        <ActionMenuItem onSelect={() => table.onFilter(property.id)}>
          {t('database.filterAction')}
        </ActionMenuItem>
        {groupable ? (
          <ActionMenuItem onSelect={() => table.onGroupBy(property.id)}>
            {t('database.groupAction')}
          </ActionMenuItem>
        ) : null}
        {isTitle ? null : (
          <ActionMenuItem onSelect={() => table.onHide(property.id)}>
            {t('database.hideInView')}
          </ActionMenuItem>
        )}

        <ActionMenuItem
          keepOpen
          className="menu__item nb dbmenu__item--split"
          aria-expanded={colorOpen}
          onSelect={() => setColorOpen((value) => !value)}
        >
          <span>{t('database.color')}</span>
          <span className="dbmenu__chev">›</span>
        </ActionMenuItem>
        {colorOpen ? (
          <div className="dbmenu__swatches">
            {ROW_BACKGROUNDS.map((background) => (
              <ActionMenuItem
                key={background.label}
                keepOpen
                className="dbmenu__swatch nb"
                title={background.label}
                aria-label={background.label}
                style={{
                  background: blockBackgroundVar(background.value) ?? 'var(--color-surface)',
                  borderColor:
                    property.color === background.value ? 'var(--color-primary)' : SWATCH_LINE,
                }}
                onSelect={() => table.onColumnColor(property.id, background.value)}
              />
            ))}
          </div>
        ) : null}

        <ActionMenuItem onSelect={() => table.onInsertColumn(property.id)}>
          {t('database.insertLeft')}
        </ActionMenuItem>
        {/* Unlike "insert left", this has always left the menu open. */}
        <ActionMenuItem keepOpen onSelect={() => table.onInsertColumn(property.id)}>
          {t('database.insertRight')}
        </ActionMenuItem>
        <ActionMenuItem onSelect={() => table.onClearColumn(property.id)}>
          {t('database.clearContents')}
        </ActionMenuItem>
        {isTitle ? null : (
          <>
            <ActionMenuItem onSelect={() => table.onDuplicateColumn(property.id)}>
              {t('database.duplicateProperty')}
            </ActionMenuItem>
            <ActionMenuItem danger onSelect={() => table.onDeleteColumn(property.id)}>
              {t('database.deleteProperty')}
            </ActionMenuItem>
          </>
        )}

        <div className="dbmenu__divider" />
        <div className="dbmenu__foot">
          <span className="dbmenu__addicon" aria-hidden="true">
            +
          </span>
          <ActionMenuInput
            className="ni dbmenu__addinput"
            value={newName}
            placeholder={t('database.newPropertyPlaceholder')}
            aria-label={t('database.newPropertyName')}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !newName.trim()) return;
              table.onAddProperty(newName.trim(), 'text');
              setNewName('');
              changeOpen(false);
            }}
          />
        </div>
        <span className="dbmenu__count">
          {t('database.propertyCount', { count: String(properties.length) })}
        </span>
      </ActionMenu>
    </>
  );
}
